/**
 * Rotations-Generator Phase 1 (G-17): Schichtmodell + Jahr + KW-Bereich →
 * week_plans (Matrix KW×Schicht→Team). Ergebnis-Summary im Dialog + Toast.
 *
 * Beim ERSTEN Start eines Schichtmodells (kein Rotations-Anker gesetzt) wird
 * zunächst die Startbelegung erfragt (StartAssignmentSheet), damit die
 * Rotation phasenrichtig startet; danach läuft die Generierung fort.
 */

import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { StartAssignmentSheet } from "@/components/planning/StartAssignmentSheet"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { isoWeekOf, todayIso, weeksInIsoYear } from "@/lib/isoWeek"
import { api, apiError } from "@/services/api"
import type { GenerateWeekPlansResponse, RotationStatus } from "@/types"

interface GeneratorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GeneratorDialog({ open, onOpenChange }: GeneratorDialogProps) {
  const queryClient = useQueryClient()
  const current = isoWeekOf(todayIso())
  const [shiftModelId, setShiftModelId] = useState("")
  const [year, setYear] = useState(String(current.year))
  const [fromWeek, setFromWeek] = useState(String(current.week))
  const [toWeek, setToWeek] = useState(String(current.week))
  const [result, setResult] = useState<GenerateWeekPlansResponse | null>(null)
  const [startOpen, setStartOpen] = useState(false)

  useEffect(() => {
    if (open) setResult(null)
  }, [open])

  const models = useQuery({
    queryKey: ["shiftModels", { size: 100 }],
    queryFn: () => api.organization.shiftModels.list({ size: 100 }),
    enabled: open,
  })

  const modelName = (models.data?.data ?? []).find((m) => m.id === shiftModelId)?.name

  // Rotations-Status des gewählten Modells (Anker gesetzt? → Startbelegung nötig?).
  const rotation = useQuery({
    queryKey: ["rotationStatus", Number(shiftModelId)],
    queryFn: () => api.planning.rotation.status(Number(shiftModelId)),
    enabled: open && shiftModelId !== "",
  })

  const maxWeek = weeksInIsoYear(Number(year) || current.year)
  const valid =
    shiftModelId !== "" &&
    Number(year) >= 2000 &&
    Number(fromWeek) >= 1 &&
    Number(toWeek) <= maxWeek &&
    Number(fromWeek) <= Number(toWeek)

  const generate = useMutation({
    mutationFn: () =>
      api.planning.weekPlans.generate({
        shiftModelId: Number(shiftModelId),
        year: Number(year),
        fromWeek: Number(fromWeek),
        toWeek: Number(toWeek),
      }),
    onSuccess: (r) => {
      setResult(r)
      queryClient.invalidateQueries({ queryKey: ["weekPlans"] })
      queryClient.invalidateQueries({ queryKey: ["rotationStatus", Number(shiftModelId)] })
      toast.success(
        `Wochenplan generiert: ${r.created} neu, ${r.skippedExisting} bereits vorhanden`
      )
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const status: RotationStatus | undefined = rotation.data

  /** „Generieren“: erst Anker prüfen — fehlt er, Startbelegung erfragen. */
  function onGenerateClick() {
    if (status && !status.hasAnchor) {
      setStartOpen(true)
      return
    }
    generate.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Wochenplan generieren (Phase 1)</DialogTitle>
          <DialogDescription>
            Rotationsformel je (KW, Schicht) → Team; idempotent, bestehende KW werden
            übersprungen (G-17).
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Schichtmodell *</Label>
            <Select value={shiftModelId} onChange={(e) => setShiftModelId(e.target.value)}>
              <option value="">— Schichtmodell wählen —</option>
              {(models.data?.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.shiftType})
                </option>
              ))}
            </Select>
          </div>

          {/* Rotationsstart-Status des gewählten Modells */}
          {shiftModelId !== "" && rotation.isSuccess && status && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              {status.hasAnchor ? (
                <p className="text-muted-foreground">
                  Rotationsstart ist gesetzt — es wird direkt generiert.
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Dieser Schichtplan wird zum ersten Mal gestartet — beim Generieren wird
                  zuerst die Startbelegung erfragt.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Jahr *</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>KW von *</Label>
              <Input
                type="number"
                min={1}
                max={maxWeek}
                value={fromWeek}
                onChange={(e) => setFromWeek(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>KW bis *</Label>
              <Input
                type="number"
                min={1}
                max={maxWeek}
                value={toWeek}
                onChange={(e) => setToWeek(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {year || "—"} hat {maxWeek} ISO-Kalenderwochen.
          </p>

          {result && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">Ergebnis</p>
              <p className="text-muted-foreground">
                {result.created} Zeilen neu · {result.skippedExisting} übersprungen ·{" "}
                {result.weeks} KW × {result.shifts} Schichten, {result.teams} Teams in Rotation
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          <Button
            disabled={!valid || generate.isPending || (shiftModelId !== "" && rotation.isLoading)}
            onClick={onGenerateClick}
          >
            Generieren
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Startbelegung erfragen, falls noch kein Anker gesetzt — danach generieren. */}
      {status && !status.hasAnchor && (
        <StartAssignmentSheet
          open={startOpen}
          onOpenChange={setStartOpen}
          modelId={Number(shiftModelId)}
          modelName={modelName}
          status={status}
          onAnchorSet={() => {
            setStartOpen(false)
            generate.mutate()
          }}
        />
      )}
    </Dialog>
  )
}
